/**
 * AuthDetectionBanner
 *
 * Shown after a spec is successfully converted/hosted when the backend
 * detected that the API requires authentication.
 *
 * The backend's `auth_requirement` field drives everything — the frontend
 * does zero parsing itself. The banner presents:
 *   - What auth type was detected (OAuth, Bearer token, API Key, etc.)
 *   - A short explanation of what that means for the user
 *   - A primary "Connect" button (or "Set up credentials")
 *   - A secondary "Skip for now / use URL directly" link
 *
 * Use `embedded` inside the same glass-strong card as spec preview / credentials.
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  KeyRound,
  ShieldCheck,
  Globe,
  X,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from "lucide-react";

// ── Types ─────────────────────────────────────────────────────────────────────

export type AuthRequirement = {
  is_required: boolean;
  /** "oauth2" | "apiKey" | "http_bearer" | "http_basic" | "none" */
  type: string;
  label: string;
  hint: string;
  schemes?: Array<{
    type: string;
    scheme?: string | null;
    name?: string | null;
    location?: string | null;
    is_complex?: boolean;
  }>;
};

type Props = {
  authRequirement: AuthRequirement;
  /** Called when user clicks Connect (scroll to credentials / open modal) */
  onConnect?: () => void;
  /** Called when user dismisses (skips) the banner */
  onSkip?: () => void;
  /**
   * OAuth-only escape hatch. When provided AND the detected type is oauth2,
   * a "Use API key instead" link is shown alongside Skip. Parent toggles a
   * credential-entry path so the user isn't locked into OAuth.
   */
  onUseApiKey?: () => void;
  /** Already connected — show "connected" state instead */
  isConnected?: boolean;
  /** Render inside parent glass card (no outer wrapper). */
  embedded?: boolean;
  /** When embedded, divider before the next block in the same card. */
  withBottomDivider?: boolean;
  /** Real OAuth button (OAuthConnectButton) — replaces the scroll-only connect handler. */
  oauthConnectSlot?: React.ReactNode;
  className?: string;
};

// ── Auth type config ──────────────────────────────────────────────────────────

type AuthConfig = {
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  borderColor: string;
  connectLabel: string;
  skipLabel: string;
};

const AUTH_CONFIGS: Record<string, AuthConfig> = {
  oauth2: {
    icon: ShieldCheck,
    iconBg: "bg-blue-50 dark:bg-blue-950/40",
    iconColor: "text-blue-600 dark:text-blue-400",
    borderColor: "border-blue-200 dark:border-blue-800/60",
    connectLabel: "Connect with OAuth",
    skipLabel: "Skip — I'll add credentials later",
  },
  apiKey: {
    icon: KeyRound,
    iconBg: "bg-amber-50 dark:bg-amber-950/40",
    iconColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-amber-200 dark:border-amber-800/60",
    connectLabel: "Add API Key",
    skipLabel: "Skip — use URL directly",
  },
  http_bearer: {
    icon: KeyRound,
    iconBg: "bg-amber-50 dark:bg-amber-950/40",
    iconColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-amber-200 dark:border-amber-800/60",
    connectLabel: "Add Bearer Token",
    skipLabel: "Skip — use URL directly",
  },
  http_basic: {
    icon: KeyRound,
    iconBg: "bg-amber-50 dark:bg-amber-950/40",
    iconColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-amber-200 dark:border-amber-800/60",
    connectLabel: "Add Credentials",
    skipLabel: "Skip — use URL directly",
  },
};

const DEFAULT_CONFIG: AuthConfig = AUTH_CONFIGS.http_bearer;

const OAUTH_HINT =
  "'Connect with OAuth' to authorise your account, or 'Skip' to use the MCP URL and add credentials later.";

function authTitle(authRequirement: AuthRequirement, isOAuth: boolean): string {
  if (isOAuth) return "OAuth needed for this API.";
  return `${authRequirement.label} detected for this API.`;
}

function authHint(authRequirement: AuthRequirement, isOAuth: boolean): string {
  if (isOAuth) return OAUTH_HINT;
  return authRequirement.hint;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AuthDetectionBanner({
  authRequirement,
  onConnect,
  onSkip,
  onUseApiKey,
  isConnected = false,
  embedded = false,
  withBottomDivider = false,
  oauthConnectSlot = null,
  className = "",
}: Props) {
  const [dismissed, setDismissed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // No auth needed — show a subtle "public API" pill
  if (!authRequirement.is_required || authRequirement.type === "none") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className={`flex items-center gap-2 text-xs font-medium text-emerald-700 dark:text-emerald-400 ${className}`}
      >
        <Globe className="w-3.5 h-3.5 flex-shrink-0" />
        No authentication needed — use the MCP URL directly
      </motion.div>
    );
  }

  if (dismissed) return null;

  const cfg = AUTH_CONFIGS[authRequirement.type] ?? DEFAULT_CONFIG;
  const IconComponent = cfg.icon;
  const isOAuth = authRequirement.type === "oauth2";
  const title = authTitle(authRequirement, isOAuth);
  const hint = authHint(authRequirement, isOAuth);

  // Connected state — brief confirmation
  if (isConnected) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex items-center gap-2 text-xs font-medium text-emerald-700 dark:text-emerald-400 ${className}`}
      >
        <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
        Authentication configured — credentials are baked into your MCP URL
      </motion.div>
    );
  }

  const inner = (
    <>
      {/* Header row */}
      <div className="flex items-start gap-3">
        <div
          className={`w-9 h-9 rounded-xl ${cfg.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}
        >
          <IconComponent className={`w-4 h-4 ${cfg.iconColor}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm font-semibold text-foreground">{title}</p>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="w-6 h-6 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors flex-shrink-0"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{hint}</p>
        </div>
      </div>

      {/* Detected type pill */}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${cfg.borderColor} ${cfg.iconBg} ${cfg.iconColor}`}
        >
          <IconComponent className="w-3 h-3" />
          {authRequirement.label}
        </span>

        {authRequirement.schemes && authRequirement.schemes.length > 0 && (
          <button
            type="button"
            onClick={() => setShowDetails(v => !v)}
            className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            {showDetails ? (
              <>
                <ChevronUp className="w-3 h-3" /> Hide details
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3" /> Show detected schemes
              </>
            )}
          </button>
        )}
      </div>

      {/* Scheme details */}
      <AnimatePresence>
        {showDetails && authRequirement.schemes && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden mt-2"
          >
            <div className="rounded-xl bg-muted/30 border border-border px-3 py-2 space-y-1">
              {authRequirement.schemes.map((s, i) => (
                <p key={i} className="text-[11px] font-mono text-muted-foreground">
                  {s.type}
                  {s.scheme ? ` · ${s.scheme}` : ""}
                  {s.name ? ` · ${s.name}` : ""}
                  {s.location ? ` (in ${s.location})` : ""}
                </p>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* OAuth extra note */}
      {isOAuth && (
        <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
          You&apos;ll need to connect an account before this MCP can work. After connecting, your
          token is encrypted and injected automatically.
        </p>
      )}

      {/* Action buttons */}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        {isOAuth && oauthConnectSlot}
        {isOAuth && !oauthConnectSlot && onConnect && (
          <motion.button
            type="button"
            onClick={onConnect}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold transition-all"
          >
            <IconComponent className="w-3.5 h-3.5" />
            {cfg.connectLabel}
          </motion.button>
        )}
        {onConnect && !isOAuth && (
          <motion.button
            type="button"
            onClick={onConnect}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold transition-all"
          >
            <IconComponent className="w-3.5 h-3.5" />
            {cfg.connectLabel}
          </motion.button>
        )}

        {isOAuth && onUseApiKey && (
          <button
            type="button"
            onClick={onUseApiKey}
            className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline underline-offset-2 transition-colors"
          >
            Use API key instead
          </button>
        )}

        <button
          type="button"
          onClick={() => {
            setDismissed(true);
            onSkip?.();
          }}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          {cfg.skipLabel}
        </button>
      </div>
    </>
  );

  if (embedded) {
    const divider = withBottomDivider ? "pb-6 mb-6 border-b border-border" : "";
    return <div className={[divider, className].filter(Boolean).join(" ")}>{inner}</div>;
  }

  return (
    <AnimatePresence>
      <motion.div
        key="auth-banner"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.35 }}
        className={`glass-strong rounded-2xl p-7 shadow-soft border border-border/60 ${className}`}
      >
        {inner}
      </motion.div>
    </AnimatePresence>
  );
}
